/**
 * Schema Zod centralizado para el formulario de clientes
 * Con validaciones condicionales según tipo_persona
 */
import { z } from 'zod'
import { cuitSchema } from '@/lib/schemas'
import { validarCUIT } from '@/lib/utils'

/**
 * Schema para email opcional
 * Si tiene valor, debe ser email válido
 */
const optionalEmailSchema = z
  .string()
  .email('El formato del email no es válido')
  .nullable()
  .optional()
  .or(z.literal(''))
  .or(z.null())

// ==========================================
// Schema Paso 1: Identidad
// ==========================================

export const stepIdentidadSchema = z
  .object({
    tipo_persona: z.enum(['fisica', 'juridica', 'sucesion_indivisa'], {
      message: 'Selecciona el tipo de persona',
    }),
    nombre: z.string().min(2, 'Mínimo 2 caracteres'),
    apellido: z.string().nullable().optional(),
    nombre_fantasia: z.string().nullable().optional(),
    cuit: cuitSchema,
    email: optionalEmailSchema,
    telefono: z.string().nullable().optional(),
    // Campos para representante legal (sucesión indivisa) o apoderado (sociedad)
    admin_nombre: z.string().nullable().optional(),
    admin_cuit: z.string().nullable().optional(),
    // Datos de Inscripción RPC (solo para juridica)
    inscripcion_numero: z.string().nullable().optional(),
    inscripcion_fojas: z.string().nullable().optional(),
    inscripcion_tomo: z.string().nullable().optional(),
    inscripcion_protocolo: z.string().nullable().optional(),
    inscripcion_fecha: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => !val || new Date(val) <= new Date(),
        'La fecha no puede ser futura'
      ),
    inscripcion_ano: z.number().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // Apellido requerido para física y sucesión
    if (data.tipo_persona !== 'juridica') {
      if (!data.apellido || data.apellido.trim().length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['apellido'],
          message: 'El apellido es requerido (mín. 2 caracteres)',
        })
      }
    }

    // Admin/Representante: requerido para sucesión, opcional para sociedad
    if (data.tipo_persona === 'sucesion_indivisa') {
      // Sucesión indivisa: campos obligatorios
      if (!data.admin_nombre || data.admin_nombre.trim().length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['admin_nombre'],
          message: 'El nombre del administrador es requerido',
        })
      }

      if (!data.admin_cuit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['admin_cuit'],
          message: 'El CUIT del administrador es requerido',
        })
      } else {
        const limpio = data.admin_cuit.replace(/\D/g, '')
        if (limpio.length !== 11) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['admin_cuit'],
            message: 'El CUIT debe tener 11 dígitos',
          })
        } else if (!validarCUIT(limpio).valido) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['admin_cuit'],
            message: 'El CUIT del administrador no es válido',
          })
        }
      }
    } else if (data.tipo_persona === 'juridica') {
      // Sociedad: campos opcionales, pero si se ingresan deben ser válidos
      if (data.admin_nombre && data.admin_nombre.trim().length > 0 && data.admin_nombre.trim().length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['admin_nombre'],
          message: 'El nombre debe tener al menos 2 caracteres',
        })
      }

      if (data.admin_cuit && data.admin_cuit.trim().length > 0) {
        const limpio = data.admin_cuit.replace(/\D/g, '')
        if (limpio.length !== 11) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['admin_cuit'],
            message: 'El CUIT debe tener 11 dígitos',
          })
        } else if (!validarCUIT(limpio).valido) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['admin_cuit'],
            message: 'El CUIT del representante no es válido',
          })
        }
      }
    }
  })

// ==========================================
// Schema Paso 2: Domicilio
// ==========================================

export const stepDomicilioSchema = z.object({
  domicilio_provincia: z.string().min(1, 'La provincia es requerida'),
  domicilio_localidad: z.string().nullable().optional(),
  domicilio_cp: z.string().nullable().optional(),
  domicilio_calle: z.string().nullable().optional(),
  domicilio_numero: z.string().nullable().optional(),
  domicilio_piso: z.string().nullable().optional(),
  domicilio_depto: z.string().nullable().optional(),
  domicilio_barrio: z.string().nullable().optional(),
  domicilio_referencia: z.string().nullable().optional(),
})

// ==========================================
// Schema Paso 3: Fiscal
// ==========================================

export const actividadEconomicaSchema = z.object({
  codigo: z.string(),
  descripcion: z.string(),
  es_principal: z.boolean().optional(),
})

/**
 * Schema para impuestos a liquidar
 * Alineado con el tipo ImpuestoLiquidar de @/types/impuestos
 */
export const impuestoLiquidarSchema = z.object({
  impuesto_id: z.string(),
  codigo: z.string(),
  notas: z.string().optional(),
})

export const stepFiscalSchema = z
  .object({
    condicion_iva: z.string().nullable().optional(),
    categoria_monotributo: z.string().nullable().optional(),
    provincia: z.string().nullable().optional(),
    actividad: z.string().nullable().optional(),
    actividades_economicas: z.array(actividadEconomicaSchema),
    regimen_iibb: z.enum(['local', 'convenio_multilateral', 'simplificado', 'no_gravado']).nullable(),
    cliente_desde: z.string().nullable(),
    cierre_ejercicio: z.string().nullable().optional(),
    impuestos_liquidar: z.array(impuestoLiquidarSchema).optional(),
    // Datos de Inscripción RPC (solo para juridica)
    inscripcion_numero: z.string().nullable().optional(),
    inscripcion_fojas: z.string().nullable().optional(),
    inscripcion_tomo: z.string().nullable().optional(),
    inscripcion_protocolo: z.string().nullable().optional(),
    inscripcion_fecha: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => !val || new Date(val) <= new Date(),
        'La fecha no puede ser futura'
      ),
    inscripcion_ano: z.number().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // Actividades económicas: al menos 1
    if (!data.actividades_economicas || data.actividades_economicas.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actividades_economicas'],
        message: 'Debe tener al menos una actividad económica',
      })
    }

    // Régimen IIBB requerido
    if (!data.regimen_iibb) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['regimen_iibb'],
        message: 'El régimen de Ingresos Brutos es requerido',
      })
    }

    // Simplificado solo para monotributistas
    if (data.regimen_iibb === 'simplificado' &&
        data.condicion_iva !== 'monotributo' &&
        data.condicion_iva !== 'monotributo_social') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['regimen_iibb'],
        message: 'Régimen simplificado solo disponible para monotributistas',
      })
    }

    // Cliente desde requerido
    if (!data.cliente_desde) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cliente_desde'],
        message: 'La fecha de inicio es requerida',
      })
    }

    // Nota: cierre_ejercicio se valida condicionalmente en clienteSchema
    // (solo requerido para tipo_persona === 'juridica')
  })

// ==========================================
// Schema Paso 4: Servicios
// ==========================================

export const servicioConPrecioSchema = z.object({
  servicio_id: z.string().min(1, 'Debe seleccionar un servicio'),
  precio: z.number().positive('El precio debe ser mayor a 0'),
  frecuencia: z.enum(['mensual', 'bimestral', 'trimestral', 'semestral', 'anual']),
  mes_inicio: z.number().min(1).max(12).optional(),
})

export const stepServiciosSchema = z
  .object({
    // undefined cuando el usuario no tiene permiso para asignar responsables.
    responsables: z.array(z.string()).optional(),
    descripcion_tarea: z.string().nullable().optional(),
    honorarios_mensuales: z.number().optional(),
    estado: z.enum(['Activo', 'Inactivo']).optional(),
    serviciosConPrecio: z.array(servicioConPrecioSchema).optional(),
    // Ids del catálogo de `etiquetas_cliente` elegidos en el wizard.
    etiquetas: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    // Responsable(s) requerido cuando el campo está en el form
    if (data.responsables !== undefined && data.responsables.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['responsables'],
        message: 'Debe asignar al menos un responsable',
      })
    }
    // Nota: la validación de servicios (servicio_id y precio) ya está en servicioConPrecioSchema
  })

// ==========================================
// Schema Paso 5: Deuda inicial (ORR-443)
// ==========================================

/**
 * Deuda que el alta carga junto con el cliente. Mismo shape que `DeudaAlta` de
 * `clientesService`: la 'mensual' sale del lote, con período y monto ya validados contra el
 * servicio al armarla; la 'esporadica' la carga el usuario y exige monto y servicio.
 */
export const deudaAltaSchema = z.object({
  servicio_id: z.string().min(1, 'Debe seleccionar un servicio'),
  concepto: z.string().min(1, 'El concepto es requerido'),
  monto: z.number().positive('El monto debe ser mayor a 0'),
  tipo_deuda: z.enum(['mensual', 'esporadica']),
  // Requerido pero nullable, igual que `DeudaAlta.mes_anio`: la mensual siempre trae período,
  // la esporádica manda null explícito.
  mes_anio: z.string().nullable(),
  fecha: z.string().optional(),
})

export const stepDeudaInicialSchema = z.object({
  deudasIniciales: z.array(deudaAltaSchema).optional(),
})

// ==========================================
// Schema Completo
// ==========================================

export const clienteSchema = z
  .object({
    // Identidad
    tipo_persona: z.enum(['fisica', 'juridica', 'sucesion_indivisa']),
    nombre: z.string().min(2, 'Mínimo 2 caracteres'),
    apellido: z.string().nullable().optional(),
    nombre_fantasia: z.string().nullable().optional(),
    cuit: cuitSchema,
    email: optionalEmailSchema,
    telefono: z.string().nullable().optional(),
    admin_nombre: z.string().nullable().optional(),
    admin_cuit: z.string().nullable().optional(),
    // Domicilio
    domicilio_provincia: z.string().min(1, 'La provincia es requerida'),
    domicilio_localidad: z.string().nullable().optional(),
    domicilio_cp: z.string().nullable().optional(),
    domicilio_calle: z.string().nullable().optional(),
    domicilio_numero: z.string().nullable().optional(),
    domicilio_piso: z.string().nullable().optional(),
    domicilio_depto: z.string().nullable().optional(),
    domicilio_barrio: z.string().nullable().optional(),
    domicilio_referencia: z.string().nullable().optional(),
    // Fiscal
    condicion_iva: z.string().nullable().optional(),
    categoria_monotributo: z.string().nullable().optional(),
    provincia: z.string().nullable().optional(),
    actividad: z.string().nullable().optional(),
    codigo_actividad_afip: z.string().nullable().optional(),
    actividades_economicas: z.array(actividadEconomicaSchema),
    regimen_iibb: z.enum(['local', 'convenio_multilateral', 'simplificado', 'no_gravado']).nullable(),
    cliente_desde: z.string().nullable(),
    cierre_ejercicio: z.string().nullable().optional(),
    impuestos_liquidar: z.array(impuestoLiquidarSchema).optional(),
    // Datos de Inscripción RPC (solo para juridica)
    inscripcion_numero: z.string().nullable().optional(),
    inscripcion_fojas: z.string().nullable().optional(),
    inscripcion_tomo: z.string().nullable().optional(),
    inscripcion_protocolo: z.string().nullable().optional(),
    inscripcion_fecha: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => !val || new Date(val) <= new Date(),
        'La fecha no puede ser futura'
      ),
    inscripcion_ano: z.number().nullable().optional(),
    // Servicios. responsables es undefined cuando el usuario no tiene permiso para asignarlos.
    responsables: z.array(z.string()).optional(),
    descripcion_tarea: z.string().nullable().optional(),
    honorarios_mensuales: z.number().optional(),
    estado: z.enum(['Activo', 'Inactivo']).optional(),
    serviciosConPrecio: z.array(servicioConPrecioSchema).optional(),
    // Ids del catálogo de `etiquetas_cliente` elegidos en el wizard.
    etiquetas: z.array(z.string()).optional(),
    // Deuda inicial (Paso 5). Solo lo manda el alta: `actualizarCompleto` no la lee.
    deudasIniciales: z.array(deudaAltaSchema).optional(),
  })
  .superRefine((data, ctx) => {
    // Responsable(s) requerido cuando el campo está en el form
    if (data.responsables !== undefined && data.responsables.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['responsables'],
        message: 'Debe asignar al menos un responsable',
      })
    }

    // Cierre ejercicio solo requerido para sociedades
    if (data.tipo_persona === 'juridica' && !data.cierre_ejercicio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cierre_ejercicio'],
        message: 'El cierre de ejercicio es requerido para sociedades',
      })
    }

    // Apellido requerido para física y sucesión
    if (data.tipo_persona !== 'juridica') {
      if (!data.apellido || data.apellido.trim().length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['apellido'],
          message: 'El apellido es requerido (mín. 2 caracteres)',
        })
      }
    }

    // Admin requerido para sucesión indivisa
    if (data.tipo_persona === 'sucesion_indivisa') {
      if (!data.admin_nombre || data.admin_nombre.trim().length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['admin_nombre'],
          message: 'El nombre del administrador es requerido',
        })
      }
      if (!data.admin_cuit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['admin_cuit'],
          message: 'El CUIT del administrador es requerido',
        })
      }
    }
  })

// ==========================================
// Tipos exportados
// ==========================================

export type ClienteFormValues = z.infer<typeof clienteSchema>
export type StepIdentidadValues = z.infer<typeof stepIdentidadSchema>
export type StepDomicilioValues = z.infer<typeof stepDomicilioSchema>
export type StepFiscalValues = z.infer<typeof stepFiscalSchema>
export type StepServiciosValues = z.infer<typeof stepServiciosSchema>
export type ServicioConPrecio = z.infer<typeof servicioConPrecioSchema>
export type ActividadEconomica = z.infer<typeof actividadEconomicaSchema>
export type ImpuestoLiquidar = z.infer<typeof impuestoLiquidarSchema>
export type DeudaAltaValues = z.infer<typeof deudaAltaSchema>
export type StepDeudaInicialValues = z.infer<typeof stepDeudaInicialSchema>

// ==========================================
// Schemas por paso (para validación parcial)
// ==========================================

export const STEP_SCHEMAS = [
  stepIdentidadSchema,
  stepDomicilioSchema,
  stepFiscalSchema,
  stepServiciosSchema,
] as const

// El alta agrega el paso de deuda inicial. La edición sigue con los 4 de siempre: la RPC de
// edición no lee `deudasIniciales`.
export const STEP_SCHEMAS_CON_DEUDA = [...STEP_SCHEMAS, stepDeudaInicialSchema] as const
